/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import { DisplayTemplate, ProductRefinerSource, ProductRefinerValue, RefinerType } from '@msdyn365-commerce/retail-proxy';
import { Button, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import RangeRefineItem, { RangeRefineItemType } from './range-refine-item';
import RefineItem from './refine-item';
import { IRefineItemCommonProps } from './refine-item.props.common';
import { IRefineItemToggleNotification } from './refine-item-toggle-notification';
import { findMatchingRefinementCriterion, isRangeType } from './utilities';
import { IDobbiesRefinerExpandedState } from '../dobbies-search-result-container';

/**
 * Default list of filter KeyNames for which search should be disabled.
 */
const DEFAULT_FILTERS_WITHOUT_SEARCH: string[] = ['Price'];

/**
 * Default minimum number of items required to show the search input field.
 */
const DEFAULT_SEARCH_THRESHOLD = 12;

/**
 * Properties associated with the RefineSubmenu component.
 */
export interface IRefineSubmenuProps {
    /**
     * Minimum number of filter options required to show the search input.
     * If not provided, defaults to 12.
     */
    refinerSearchThreshold?: number;

    /**
     * List of filter KeyNames for which search should be disabled.
     * If not provided, defaults to ['Price'].
     */
    refinersWithoutSearch?: string[];
    tempRangeTypeTODO: RangeRefineItemType;
    minValueSliderThumbAriaLabel?: string;
    maxValueSliderThumbAriaLabel?: string;
    productRefinerHierarchy: IProductRefinerHierarchy;
    selectedRefinerValues: ProductRefinerValue[];
    refineItemCommonProps: IRefineItemCommonProps;
    isDisabled: boolean;
    isExpandedOnInitialLoad: boolean;
    context: ICoreContext<IGeneric<IAny>>;
    moduleId: string;
    moduleTypeName: string;
    productCountAriaLabel?: string;
    singleProductCountAriaLabel?: string;
    isMobile?: boolean;
    refinersExpandedStateData?: IDobbiesRefinerExpandedState[][];
    onToggle?(refinerRecId: string | undefined, isExpanded: boolean): void;
    /**
     * Controlled expanded state from parent. If provided, this takes precedence over local state.
     */
    controlledExpanded?: boolean;
    /**
     * Callback when expand state changes. Used with controlledExpanded for controlled mode.
     */
    onExpandChange?(keyName: string | undefined, isExpanded: boolean): void;

    /**
     * The telemetry content
     */
    telemetryContent?: ITelemetryContent;
    onUpdateRefiners(notfication: Readonly<IRefineItemToggleNotification>): void;
    urlBuilder(refiner: IRefineItemToggleNotification): string;
    /**
     * Callback to notify parent when range validation error state changes.
     * @param hasError - True if there is a validation error, false otherwise.
     */
    onRangeValidationErrorChange?: (hasError: boolean) => void;
}

/**
 * Refine submenu state.
 */
export interface IRefineSubmenuState extends React.ComponentState {
    expanded: boolean;
    visibleItemCount: number;
    searchQuery: string;
}

/**
 *
 * The RefineSubmenu component renders the submenu category and child items.
 * This computed observes the stateful category hierarchy object.
 * @extends {React.PureComponent<IRefineSubmenuProps>}
 */
class RefineSubmenu extends React.Component<IRefineSubmenuProps, IRefineSubmenuState> {
    constructor(props: IRefineSubmenuProps) {
        super(props);

        this._onToggleItem = this._onToggleItem.bind(this);
        this._onToggleSubmenu = this._onToggleSubmenu.bind(this);
        this._onShowMore = this._onShowMore.bind(this);
        this._onSearchChange = this._onSearchChange.bind(this);

        //const isExpanded = this.props.isExpandedOnInitialLoad;

        // if (this.props.productRefinerHierarchy.DataTypeValue === ProductRefinerValueDataTypeValue.Range) {
        //     isExpanded = true;
        // }

        this.state = {
            // expanded: isExpanded
            expanded: true,
            visibleItemCount: 6,
            searchQuery: ''
        };
    }

    public shouldComponentUpdate(nextProps: IRefineSubmenuProps, nextState: IRefineSubmenuState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public componentDidUpdate(prevProps: IRefineSubmenuProps, prevState: IRefineSubmenuState): void {
        // Only sync state if refinersExpandedStateData actually changed (different object reference)
        // and contains valid data for this component
        if (
            this.props.refinersExpandedStateData &&
            this.props.refinersExpandedStateData.length &&
            prevProps.refinersExpandedStateData !== this.props.refinersExpandedStateData
        ) {
            // Find the expanded state for this refiner by keyName
            for (let i = 0; i < this.props.refinersExpandedStateData.length; i++) {
                for (let j = 0; j < this.props.refinersExpandedStateData[i].length; j++) {
                    const entry = this.props.refinersExpandedStateData[i][j];
                    if (entry && entry.keyName === this.props.productRefinerHierarchy.KeyName && this.state.expanded !== entry.isExpanded) {
                        this.setState({ expanded: entry.isExpanded });
                        return; // Exit after finding and updating
                    }
                }
            }
        }
    }

    public render(): JSX.Element | null {
        const { productRefinerHierarchy, refineItemCommonProps } = this.props;

        if (!productRefinerHierarchy) {
            refineItemCommonProps.telemetry.error('Cannot render submenu without refiner hierarchy data');
        }

        if (productRefinerHierarchy.SourceValue === ProductRefinerSource.Rating && this.props.context.app.config.hideRating) {
            return null;
        }
        // VSI CUSTOMIZATION START
        const currentUrl = this.props.context.request.url.requestUrl.pathname;
        if (currentUrl.includes('edits')) {
            if (productRefinerHierarchy.KeyName === 'Category') {
                return null;
            }
        } else {
            if (productRefinerHierarchy.KeyName === 'Department') {
                return null;
            }
        }
        // VSI CUSTOMIZATION END

        // Get selected values for this filter to display when collapsed
        const selectedValuesDisplay = this._getSelectedValuesDisplay();

        // Use controlled expanded state if provided, otherwise use local state
        const isExpanded = this.props.controlledExpanded !== undefined ? this.props.controlledExpanded : this.state.expanded;

        return (
            <div className={`ms-refine-submenu list-group ${productRefinerHierarchy.KeyName}`}>
                <Button
                    className={isExpanded ? 'ms-refine-submenu__toggle_collapsed' : 'ms-refine-submenu__toggle_expanded'}
                    aria-label={productRefinerHierarchy.KeyName || 'refiner.Name'}
                    onClick={this._onToggleSubmenu}
                    aria-expanded={isExpanded}
                >
                    {productRefinerHierarchy.KeyName}
                </Button>
                {isExpanded && selectedValuesDisplay && <div className='ms-refine-submenu__selected-values'>{selectedValuesDisplay}</div>}
                <ul className={isExpanded ? 'slide-up' : 'slide-down'}>{this._renderChildItems(productRefinerHierarchy)}</ul>
            </div>
        );
    }

    private _renderChildItems(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        if (isRangeType(productRefinerHierarchy.DataTypeValue)) {
            return this._renderRange(productRefinerHierarchy);
        }
        return this._renderSingleMultiSelect(productRefinerHierarchy);
    }

    private _renderSingleMultiSelect(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        const { isDisabled, refineItemCommonProps, selectedRefinerValues, context } = this.props;
        const isSingleSelect =
            productRefinerHierarchy.RefinerTypeValue === RefinerType.SingleSelect &&
            productRefinerHierarchy.SourceValue === ProductRefinerSource.Rating;
        const role = isSingleSelect ? { role: 'radiogroup' } : undefined;
        const refinerValuesList = productRefinerHierarchy.Values || [];

        // Determine if search should be shown for this filter
        const filterKeyName = productRefinerHierarchy.KeyName || '';
        const filtersWithoutSearch = this.props.refinersWithoutSearch ?? DEFAULT_FILTERS_WITHOUT_SEARCH;
        const searchThreshold = this.props.refinerSearchThreshold ?? DEFAULT_SEARCH_THRESHOLD;
        const isSearchExcluded = filtersWithoutSearch.some(excludedFilter =>
            filterKeyName.toLowerCase().includes(excludedFilter.toLowerCase())
        );
        const showSearch = refinerValuesList.length > searchThreshold && !isSearchExcluded;

        // Filter items based on search query
        const filteredRefinerValues = this.state.searchQuery
            ? refinerValuesList.filter((child: ProductRefinerValue) => {
                  const label = child.LeftValueBoundLocalizedString || child.LeftValueBoundString || '';
                  return label.toLowerCase().includes(this.state.searchQuery.toLowerCase());
              })
            : refinerValuesList;

        const listItems = filteredRefinerValues.map((child: ProductRefinerValue, index: number) => {
            if (!child) {
                refineItemCommonProps.telemetry.error(
                    `[refine-submenu] Could not render refine item for refiner ${productRefinerHierarchy.RecordId} (${productRefinerHierarchy.KeyName})`
                );
                return null;
            }

            const selectedRefinementCriterion = findMatchingRefinementCriterion(child, selectedRefinerValues);

            return (
                <RefineItem
                    parentProductRefinerHierarchy={productRefinerHierarchy}
                    productRefinerValue={child}
                    selectedRefinementCriterion={selectedRefinementCriterion}
                    selectedRefinerValues={selectedRefinerValues.filter(selectedValue =>
                        refinerValuesList.find(value => value.RefinerRecordId === selectedValue.RefinerRecordId)
                    )}
                    refineItemCommonProps={refineItemCommonProps}
                    onToggle={this._onToggleItem}
                    urlBuilder={this.props.urlBuilder}
                    isDisabled={isDisabled}
                    key={index}
                    index={index}
                    context={context}
                    moduleId={this.props.moduleId}
                    moduleTypeName={this.props.moduleTypeName}
                    telemetryContent={this.props.telemetryContent}
                />
            );
        });

        const visibleItems = listItems.slice(0, this.state.visibleItemCount);
        const remainingCount = Math.max(0, listItems.length - visibleItems.length);
        const hasNoSearchResults = this.state.searchQuery && filteredRefinerValues.length === 0;

        return (
            <>
                {showSearch && (
                    <div className='ms-refine-submenu__search'>
                        <input
                            type='text'
                            className='ms-refine-submenu__search-input'
                            placeholder='Search...'
                            value={this.state.searchQuery}
                            onChange={this._onSearchChange}
                            aria-label={`Search ${productRefinerHierarchy.KeyName}`}
                        />
                        <span className='ms-refine-submenu__search-icon' aria-hidden='true' />
                    </div>
                )}
                {hasNoSearchResults ? (
                    <p className='ms-refine-submenu__no-results'>No matches found.</p>
                ) : (
                    <ul
                        className={`ms-refine-submenu__list ${productRefinerHierarchy.KeyName}`}
                        {...role}
                        aria-label={productRefinerHierarchy.KeyName}
                    >
                        {visibleItems}
                    </ul>
                )}
                {remainingCount > 0 && !this.state.searchQuery && (
                    <Button className='ms-refine-submenu__show-more' onClick={this._onShowMore} type='button'>
                        {`+ more (${remainingCount})`}
                    </Button>
                )}
            </>
        );
    }

    private _getRangeType(productRefinerHierarchy: IProductRefinerHierarchy): 'input' | 'slider' {
        if (
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Range ||
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Slider ||
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.SliderWithBars
        ) {
            return 'slider';
        }
        return 'input';
    }

    private _renderRange(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        const {
            isDisabled,
            refineItemCommonProps,
            selectedRefinerValues,
            context,
            minValueSliderThumbAriaLabel,
            maxValueSliderThumbAriaLabel
        } = this.props;
        const submenuClassNamePrefix = 'ms-refine-submenu__item list-group-item refine-submenu__item';
        const refinerValuesList = productRefinerHierarchy.Values || [];

        // For range type refiners (like Price), only render a single slider
        // Use the first value which contains the overall min/max bounds
        if (refinerValuesList.length === 0) {
            return null;
        }

        const child = refinerValuesList[0];
        if (!child) {
            refineItemCommonProps.telemetry.error(
                `Could not render refine item for refiner ${productRefinerHierarchy.RecordId} (${productRefinerHierarchy.KeyName})`
            );
            return null;
        }

        const selectedRefinementCriterion = findMatchingRefinementCriterion(child, selectedRefinerValues);
        const rangeType = this._getRangeType(productRefinerHierarchy);
        const key = selectedRefinementCriterion
            ? `${selectedRefinementCriterion.LeftValueBoundString}-${selectedRefinementCriterion.RightValueBoundString}`
            : `not-selected-0`;

        return (
            <ul className={`ms-refine-submenu__list list-unstyled ${productRefinerHierarchy.KeyName}`}>
                <li className={`${submenuClassNamePrefix}--range`} key={productRefinerHierarchy.RecordId}>
                    <RangeRefineItem
                        parentProductRefinerHierarchy={productRefinerHierarchy}
                        productRefinerValue={child}
                        selectedRefinementCriterion={selectedRefinementCriterion}
                        refineItemCommonProps={refineItemCommonProps}
                        onToggle={this._onToggleItem}
                        urlBuilder={this.props.urlBuilder}
                        isDisabled={isDisabled}
                        rangeType={rangeType}
                        key={productRefinerHierarchy.RecordId}
                        sliderKey={key}
                        context={context}
                        minValueSliderThumbAriaLabel={minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={maxValueSliderThumbAriaLabel}
                        moduleId={this.props.moduleId}
                        moduleTypeName={this.props.moduleTypeName}
                        onValidationErrorChange={this.props.onRangeValidationErrorChange}
                    />
                </li>
            </ul>
        );
    }

    private _onToggleItem(itemToggleNotification: IRefineItemToggleNotification): void {
        this.props.onUpdateRefiners(itemToggleNotification);
    }

    /**
     * Gets the display string for selected values in this filter.
     * Returns null if no values are selected.
     * For range filters (like Price), returns the range format.
     * For multi-select filters, returns values separated by |.
     */
    private _getSelectedValuesDisplay(): React.ReactNode {
        const { productRefinerHierarchy, selectedRefinerValues, context } = this.props;
        const refinerValuesList = productRefinerHierarchy.Values || [];

        // Find selected values that belong to this refiner
        const selectedForThisRefiner = selectedRefinerValues.filter(selectedValue =>
            refinerValuesList.some(value => value.RefinerRecordId === selectedValue.RefinerRecordId)
        );

        if (selectedForThisRefiner.length === 0) {
            return null;
        }

        // Handle range type (like Price)
        if (isRangeType(productRefinerHierarchy.DataTypeValue)) {
            const rangeValue = selectedForThisRefiner[0];
            if (rangeValue.LeftValueBoundString !== undefined && rangeValue.RightValueBoundString !== undefined) {
                // Format as currency if it's a price filter (without decimals)
                try {
                    const currencyCode = context.request.channel?.Currency || '';
                    const minValue = Math.round(Number(rangeValue.LeftValueBoundString));
                    const maxValue = Math.round(Number(rangeValue.RightValueBoundString));
                    const minFormatted = context.cultureFormatter.formatCurrency(minValue, currencyCode).replace(/\.00$/, '');
                    const maxFormatted = context.cultureFormatter.formatCurrency(maxValue, currencyCode).replace(/\.00$/, '');
                    return `${minFormatted} - ${maxFormatted}`;
                } catch {
                    return `${rangeValue.LeftValueBoundString} - ${rangeValue.RightValueBoundString}`;
                }
            }
            return null;
        }

        // Handle regular filters (multi-select or single-select)
        const displayValues = selectedForThisRefiner
            .map(value => value.LeftValueBoundLocalizedString || value.LeftValueBoundString || '')
            .filter(v => v);

        if (displayValues.length === 0) {
            return null;
        }

        return displayValues.map((value, index) => (
            <React.Fragment key={index}>
                {index > 0 && <span>|</span>}
                {value}
            </React.Fragment>
        ));
    }

    private _onShowMore(): void {
        const totalItems = this.props.productRefinerHierarchy.Values?.length || 0;
        this.setState({ visibleItemCount: totalItems });
    }

    private _onToggleSubmenu(): void {
        // Calculate the new expanded state
        const currentExpanded = this.props.controlledExpanded !== undefined ? this.props.controlledExpanded : this.state.expanded;
        const newExpanded = !currentExpanded;

        // Call controlled mode callback if provided
        if (this.props.onExpandChange) {
            this.props.onExpandChange(this.props.productRefinerHierarchy.KeyName, newExpanded);
        }

        // Call legacy onToggle if provided (for backward compatibility)
        if (this.props.onToggle) {
            this.props.onToggle(this.props.productRefinerHierarchy.KeyName, newExpanded);
        }

        // Update local state (will be ignored if in controlled mode)
        this.setState({ expanded: newExpanded });
    }

    private _onSearchChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ searchQuery: event.target.value });
    }
}

export default RefineSubmenu;
